/*
 * The drive's routes (S-06 §3.1): `/drive` (Recent), the fixed pages of `DRIVE_PAGES`, and
 * `/drive/<key>` with an ignored `/<slug>` after it, which opens whatever the key names — or a
 * document's own address, `/drive/<link id>`, with the same slug and its tab in `?tab=` (DV W-1 D1,
 * W2-D6), which the same route takes (`open.tsx` tells the two apart). They are
 * created against the shell's layout route, which arrives as the argument of `FrontendModule.routes`: a
 * module never imports the router tree it is mounted into (modules.md §8). The administrators' Other
 * shared drives is a page of core's administration frame (`index.ts`, the `adminPages` slot).
 *
 * The list resolves at once and each screen behind it is a `lazy()` (`screens.ts`), because this file
 * is statically reachable from `main.tsx`. A segment is checked against the key and link-id grammars
 * before anything is read: a malformed one is the drive's dead end without a request, the same page an
 * unknown key and one the reader cannot open end on.
 *
 * `/drive/search` is the Search place with the drive's section (S-09): the term is never part of an
 * address — it would reach the history, the Referer and every access log — so the drive's own search
 * field hands it to the Search page in memory, and this address only leads there.
 */

import { createRoute, redirect, useRouterState, type AnyRoute } from "@tanstack/react-router"
import type { ComponentType } from "react"
import { useShortcutScope } from "../../core/shell/index.ts"
import { DRIVE_HOME, DRIVE_PAGES, keyOfPath, type DrivePage } from "./navigation.ts"
import {
	MyDriveScreen,
	NoAccessScreen,
	OpenScreen,
	RecentScreen,
	StarredScreen,
	TrashScreen,
} from "./screens.ts"
import { DRIVE_SCOPE } from "./shortcuts.ts"

/** The screen of each fixed page but search, which is the Search place's. */
const PAGE_SCREENS = {
	recent: RecentScreen,
	starred: StarredScreen,
	trash: TrashScreen,
	my: MyDriveScreen,
} satisfies Record<Exclude<DrivePage, "search">, ComponentType>

/**
 * A list page of the drive, with the `drive` shortcut scope open while it is on route — New's keys and
 * a row's. It is pushed here and not in the page, as chat's and AI chats' scopes are: the page is
 * rendered bare in its unit tests.
 */
function ListPage({ Screen }: { Screen: ComponentType }) {
	useShortcutScope(DRIVE_SCOPE)
	return <Screen />
}

/** An address that names something: its key or link id read off the location, or the dead end. */
function ItemRoute() {
	const key = useRouterState({ select: (state) => keyOfPath(state.location.pathname) })
	return key === "" ? <NoAccessScreen /> : <OpenScreen itemKey={key} />
}

/** The module's routes, built against the parent the shell hands in. */
export function driveRoutes(parent: AnyRoute): AnyRoute[] {
	const deadEnd = { notFoundComponent: NoAccessScreen }
	const pages = Object.entries(PAGE_SCREENS).map(([page, Screen]) =>
		createRoute({
			getParentRoute: () => parent,
			path: `${DRIVE_HOME}/${DRIVE_PAGES[page as keyof typeof PAGE_SCREENS]}`,
			component: () => <ListPage Screen={Screen} />,
			...deadEnd,
		}),
	)
	return [
		createRoute({
			getParentRoute: () => parent,
			path: DRIVE_HOME,
			component: () => <ListPage Screen={RecentScreen} />,
			...deadEnd,
		}),
		...pages,
		createRoute({
			getParentRoute: () => parent,
			path: `${DRIVE_HOME}/${DRIVE_PAGES.search}`,
			beforeLoad: () => {
				// redirect() is TanStack's control-flow signal for a route, not an error condition.
				// eslint-disable-next-line @typescript-eslint/only-throw-error
				throw redirect({ to: "/search" })
			},
		}),
		createRoute({
			getParentRoute: () => parent,
			path: `${DRIVE_HOME}/$key`,
			component: ItemRoute,
			...deadEnd,
		}),
		// A document's name after its key — a link that carries a slug — opens the same item.
		createRoute({
			getParentRoute: () => parent,
			path: `${DRIVE_HOME}/$key/$slug`,
			component: ItemRoute,
		}),
	] as AnyRoute[]
}
